/**
 * WebSocket Hook - 为React组件提供WebSocket连接管理
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import websocketService from '../services/websocketService';

export const useWebSocket = () => {
  const [connectionState, setConnectionState] = useState('CLOSED');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [chartData, setChartData] = useState({});
  const [priceData, setPriceData] = useState({});
  const [error, setError] = useState(null);
  
  // 使用ref避免重复注册事件监听器
  const listenersRegistered = useRef(false);

  /**
   * 处理图表数据更新
   */
  const handleChartUpdate = useCallback((data) => {
    setChartData(prev => ({
      ...prev,
      [data.symbol]: {
        price: data.price,
        timestamp: data.timestamp,
        lastUpdate: Date.now()
      }
    }));
    setLastUpdate(Date.now());
  }, []);

  /**
   * 处理价格数据更新
   */
  const handlePriceUpdate = useCallback((data) => {
    console.log('💰 价格数据更新:', data);
    setPriceData(prev => ({
      ...prev,
      [data.symbol]: {
        price: data.price,
        timestamp: data.timestamp,
        lastUpdate: Date.now()
      }
    }));
    setLastUpdate(Date.now());
  }, []);

  /**
   * 处理连接状态变化
   */
  const handleConnectionChange = useCallback((state) => {
    setConnectionState(state);
    setError(null);
  }, []);

  /**
   * 处理连接错误
   */
  const handleError = useCallback((error) => {
    setError(error);
  }, []);

  /**
   * 连接WebSocket
   */
  const connect = useCallback(() => {
    websocketService.connect();
  }, []);

  /**
   * 断开WebSocket
   */
  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  /**
   * 获取特定交易对的最新价格
   */
  const getLatestPrice = useCallback((symbol) => {
    return priceData[symbol]?.price || chartData[symbol]?.price || null;
  }, [priceData, chartData]);

  /**
   * 获取特定交易对的图表数据
   */
  const getChartData = useCallback((symbol) => {
    return chartData[symbol] || null;
  }, [chartData]);

  // 组件挂载时设置事件监听器和连接
  useEffect(() => {
    if (!listenersRegistered.current) {
      // 注册事件监听器
      websocketService.on('connected', () => handleConnectionChange('OPEN'));
      websocketService.on('disconnected', () => handleConnectionChange('CLOSED'));
      websocketService.on('error', handleError);
      websocketService.on('chartUpdate', handleChartUpdate);
      websocketService.on('priceUpdate', handlePriceUpdate);
      websocketService.on('maxReconnectAttemptsReached', () => {
        setError(new Error('WebSocket连接失败，已达到最大重连次数'));
      });

      listenersRegistered.current = true;
    }

    // 自动连接
    connect();

    // 清理函数
    return () => {
      if (listenersRegistered.current) {
        websocketService.off('connected', () => handleConnectionChange('OPEN'));
        websocketService.off('disconnected', () => handleConnectionChange('CLOSED'));
        websocketService.off('error', handleError);
        websocketService.off('chartUpdate', handleChartUpdate);
        websocketService.off('priceUpdate', handlePriceUpdate);
        listenersRegistered.current = false;
      }
    };
  }, [connect, handleConnectionChange, handleError, handleChartUpdate, handlePriceUpdate]);

  // 定期更新连接状态
  useEffect(() => {
    const interval = setInterval(() => {
      const currentState = websocketService.getConnectionState();
      if (currentState !== connectionState) {
        setConnectionState(currentState);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [connectionState]);

  return {
    // 连接状态
    connectionState,
    isConnected: connectionState === 'OPEN',
    isConnecting: connectionState === 'CONNECTING',
    error,
    
    // 数据
    chartData,
    priceData,
    lastUpdate,
    
    // 方法
    connect,
    disconnect,
    getLatestPrice,
    getChartData,
    
    // 统计信息
    connectedSymbols: Object.keys(chartData).length,
    totalUpdates: Object.keys(chartData).length + Object.keys(priceData).length
  };
};

export default useWebSocket;
